import { makeStyles } from '@mui/styles';
import FileUpload from 'apollo-react/components/FileUpload';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import qs from 'query-string';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { setOpenStepFormTabEntityDisplay } from '../../actions/EntityDisplayActions';
import { ENTITY_DISPLAY } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { getAppSettingsByKey } from '../../selectors/AppConfigsSelectors';
import { isFileTypeAllowed, transformFileFormatsToAccept } from '../../utils/commonUtils';
import { uploadAttachments } from '../actions/file-upload.actions';
import { saveAttachmentsForLocalDB } from '../actions/file-upload.offline.actions';
import { setUserMadeInteraction } from '../actions/runtime.actions';
import { FormContext } from '../context';
import { getNetworkStatus } from '../selectors/common.selectors';

const useStyles = makeStyles({
  modal: {
    width: 600,
  },
});

const AttachmentModal = ({ open, onClose, entityName, propsFormKey, gridId, parentData }) => {
  const classes = useStyles();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const formKey = useContext(FormContext);
  const translateString = useTranslation();
  const supportedFileExtensions = useSelector(
    getAppSettingsByKey('Attachments/SupportedFileExtensions')
  );
  const { isMobileDevice } = useWindowDimensions();
  const { pathname, search } = useLocation();
  const isOnline = useSelector(getNetworkStatus);

  const isEntityDisplay = [ENTITY_DISPLAY, ENTITY_DISPLAY].includes(pathname);

  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
    }
  }, [open]);

  const handleUpload = (files) => {
    const validatedFiles = files.map((file) => {
      if (!isFileTypeAllowed(supportedFileExtensions, file)) {
        file.errorMessage = translateString(
          'File will not be uploaded. Extension is not supported'
        );
      }
      return file;
    });

    setSelectedFiles([...selectedFiles, ...validatedFiles]);
  };

  const handleDelete = (file) => {
    setSelectedFiles(selectedFiles.filter((item) => item.name !== file.name));
  };

  const handleSave = () => {
    if (selectedFiles.length) {
      dispatch(setUserMadeInteraction(propsFormKey ?? formKey));
    }

    setIsLoading(true);
    let requests;

    if (isOnline) {
      requests = selectedFiles.map((file) =>
        dispatch(uploadAttachments(file, entityName, propsFormKey ?? formKey, gridId, parentData))
      );
    } else {
      const qsParams = qs.parse(search) || {};
      const entityId = `${qsParams.itmId}_${qsParams.stepId}`;

      requests = selectedFiles.map((file) =>
        dispatch(
          saveAttachmentsForLocalDB(
            entityId,
            file,
            entityName,
            propsFormKey ?? formKey,
            gridId,
            parentData
          )
        )
      );
    }

    if (isEntityDisplay && isMobileDevice) {
      dispatch(setOpenStepFormTabEntityDisplay(true));
    }

    Promise.all(requests).finally(() => {
      setIsLoading(false);
      setSelectedFiles([]);
      onClose();
    });
  };

  return (
    <Modal
      open={open}
      variant="default"
      onClose={onClose}
      title={translateString('Upload a File')}
      buttonProps={[
        { label: translateString('Cancel'), 'ui-ta': 'UploadFileModal-CancelButton' },
        {
          label: translateString('Upload'),
          onClick: handleSave,
          disabled: selectedFiles.length === 0 || selectedFiles.some((file) => !!file.errorMessage),
          'ui-ta': 'UploadFileModal-UploadButton',
        },
      ]}
      PaperProps={{ style: { margin: isMobileDevice ? 16 : 32 } }}
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <FileUpload
        fullWidth
        value={selectedFiles}
        onFileDelete={handleDelete}
        onUpload={handleUpload}
        accept={transformFileFormatsToAccept(supportedFileExtensions)}
        dropAreaLabels={{
          activeText: translateString('Drop here to upload!'),
          browseLinkText: translateString('browse').toLowerCase(),
          defaultText: `${translateString('Drag files here or')} `,
          hoverText: translateString('Click to browse'),
        }}
      />
      {isLoading && <Loader isInner />}
    </Modal>
  );
};

export default AttachmentModal;
